import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Link } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { find, findIndex, first, size, some } from 'es-toolkit/compat';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import useMainFilters from 'components/SearchFilters/hooks/useMainFilters';
import { SlidePreviewPanel } from 'components/atoms/SlidePreviewPanel';
import { SlidePreviewSummaryRow } from 'components/atoms/SlidePreviewSummaryRow';
import { UnwoundRowMetadata } from 'interfaces/genericFields/unwindRowsWithInnerArrays';
import { Procedure } from 'interfaces/procedure';
import { Slide } from 'interfaces/slide';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { SlidePageUrlProps, useNavigationToViewerPage } from 'utils/useNavigationToViewerPage';
import { LinkToCaseViewerComponent } from './CaseIdCellRenderer';

interface SlideIdLinkProps extends Partial<SlidePageUrlProps> {
  slideId: string;
  formattedValue?: string;
  target?: string;
  rel?: string;
  asButton?: boolean;
}

interface SlidePreviewCellProps {
  slides?: Slide[];
  caseId?: number;
  caseStudyId?: string;
  labId?: string;
  slidesMode?: boolean;
  summaryLabel?: string;
  summaryContent?: React.ReactNode;
  openViewerLink?: {
    href: string;
    label?: string;
    target?: string;
    rel?: string;
  };
}

type SlidePreviewRow = Procedure & {
  caseId?: number | string;
  isPendingSlideMockCase?: boolean;
  _unwoundRowMetadata?: Pick<UnwoundRowMetadata, 'baseRowId'>;
};

const toViewerCaseId = (rawCaseId: unknown): number | undefined => {
  const numericCaseId = Number(rawCaseId);
  return Number.isFinite(numericCaseId) ? numericCaseId : undefined;
};

const getViewerCaseId = (row: SlidePreviewRow): number | undefined => {
  if (row.isPendingSlideMockCase) {
    return undefined;
  }

  return toViewerCaseId(row.caseId ?? row._unwoundRowMetadata?.baseRowId ?? row.id);
};

export const SlidePreviewCell: React.FC<SlidePreviewCellProps> = ({
  slides,
  caseId,
  caseStudyId,
  labId,
  slidesMode,
  summaryLabel,
  summaryContent,
  openViewerLink,
}) => {
  const [open, setOpen] = React.useState(false);
  const [selectedSlideId, setSelectedSlideId] = React.useState<string | null>(first(slides)?.id ?? null);
  const openPreview = (slideId?: string) => {
    setSelectedSlideId(slideId ?? first(slides)?.id ?? null);
    setOpen(true);
  };

  React.useEffect(() => {
    if (!some(slides, ({ id }) => id === selectedSlideId)) {
      setSelectedSlideId(first(slides)?.id ?? null);
    }
  }, [slides, selectedSlideId]);

  const selectedSlide = find(slides, ({ id }) => id === selectedSlideId) ?? first(slides);
  if (!selectedSlide) {
    return <SlidePreviewSummaryRow slides={slides} onOpenPreview={openPreview} />;
  }

  const indexOfSelectedSlide = findIndex(slides, ({ id }) => id === selectedSlideId);
  const shouldUseSlideViewerLink = slidesMode || !caseId;

  return (
    <>
      <SlidePreviewSummaryRow
        slides={slides}
        summaryLabel={summaryLabel}
        summaryContent={summaryContent}
        onOpenPreview={openPreview}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {size(slides) === 1
            ? `Slide preview: ${selectedSlide.id}`
            : `Slide preview ${indexOfSelectedSlide + 1} of ${size(slides)}: ${selectedSlide.id}`}
        </DialogTitle>
        <DialogContent dividers>
          <SlidePreviewPanel slides={slides} selectedSlideId={selectedSlideId} onSelectSlide={setSelectedSlideId} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          {openViewerLink ? (
            <Button
              href={openViewerLink.href}
              target={openViewerLink.target || '_blank'}
              rel={openViewerLink.rel || 'noopener noreferrer'}
              variant="outlined"
              endIcon={
                (openViewerLink.target || '_blank') === '_blank' ? <OpenInNewIcon fontSize="small" /> : undefined
              }
            >
              {openViewerLink.label || 'Open slide in viewer'}
            </Button>
          ) : shouldUseSlideViewerLink ? (
            <LinkToSlideViewerComponent
              slideId={selectedSlide.id}
              caseId={caseId}
              caseStudyId={caseStudyId}
              labId={labId}
              selectedSlideIds={[selectedSlide.id]}
              viewersStainPreferences={selectedSlide.stainingType ? [selectedSlide.stainingType] : undefined}
              formattedValue="Open slide in viewer"
              target="_blank"
              rel="noopener"
              asButton
            />
          ) : (
            <LinkToCaseViewerComponent
              caseId={caseId}
              caseStudyId={caseStudyId}
              labId={labId}
              slideIdForReviewing={selectedSlide.id}
              selectedSlideIds={[selectedSlide.id]}
              viewersStainPreferences={selectedSlide.stainingType ? [selectedSlide.stainingType] : undefined}
              formattedValue="Open case in viewer"
              target="_blank"
              rel="noopener"
              asButton
            />
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export const LinkToSlideViewerComponent: React.FC<SlideIdLinkProps> = ({
  formattedValue,
  target,
  rel,
  asButton,
  labId: slidePageLabId,
  ...slidePageUrlProps
}) => {
  const { labId, queryLabId } = useCurrentLabId();
  const { queryParams } = useEncodedFilters();
  const { isStudyIdSpecific } = useMainFilters();
  const studyIdIsSpecific = isStudyIdSpecific(queryParams?.filters?.studyId);
  const resolvedLabId = queryLabId || labId || slidePageLabId;

  const { getLinkToSlidePage } = useNavigationToViewerPage();
  const linkTo = getLinkToSlidePage({
    caseStudyId: studyIdIsSpecific ? queryParams?.filters?.studyId : undefined,
    ...slidePageUrlProps,
    labId: resolvedLabId,
  });
  const label = formattedValue || slidePageUrlProps.slideId;

  if (asButton) {
    return (
      <Button
        component={RouterLink}
        to={linkTo}
        target={target}
        rel={rel}
        variant="outlined"
        endIcon={target === '_blank' ? <OpenInNewIcon fontSize="small" /> : undefined}
      >
        {label}
      </Button>
    );
  }

  return (
    <Link
      to={linkTo}
      target={target}
      rel={rel}
      component={RouterLink}
      color="secondary"
      sx={{ display: 'flex', alignItems: 'center' }}
    >
      {target == '_blank' && <OpenInNewIcon fontSize="small" sx={{ mr: 0.5 }} />}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
    </Link>
  );
};

export const renderSlideIdCell: GridColDef<any, string>['renderCell'] = ({ value, row }): ReturnType<React.FC> => {
  const slideId = value;
  const caseId = getViewerCaseId(row as SlidePreviewRow);

  return (
    <SlidePreviewCell
      slides={[{ ...row, id: slideId } as Slide]}
      caseId={caseId}
      caseStudyId={row.studyId}
      labId={row.labId}
      slidesMode
    />
  );
};

export const renderSlideIdsCell: GridColDef<Procedure, string[]>['renderCell'] = ({ row }): ReturnType<React.FC> => {
  return (
    <SlidePreviewCell
      slides={row.slides}
      caseId={getViewerCaseId(row as SlidePreviewRow)}
      caseStudyId={row.studyId}
      labId={row.labId}
    />
  );
};

export const getRenderSlideIdCell = (
  overrides: Partial<SlideIdLinkProps> & {
    formattedValue?: string;
    slideIdColumn?: string;
    labIdColumn?: string;
    caseStudyIdColumn?: string;
    caseIdColumn?: string;
  }
): GridColDef<any, string>['renderCell'] => {
  return (renderCellParams): ReturnType<React.FC> => {
    const slideId = overrides.slideId
      ? overrides.slideId
      : overrides.slideIdColumn
      ? renderCellParams.row[overrides.slideIdColumn]
      : renderCellParams.value;
    const formattedValue = overrides.formattedValue || renderCellParams.formattedValue;
    const labId = overrides.labId || renderCellParams.row[overrides.labIdColumn || 'labId'];
    const caseStudyId = overrides.caseStudyId || renderCellParams.row[overrides.caseStudyIdColumn || 'studyId'];
    const hasExplicitCaseId = overrides.caseId !== undefined || Boolean(overrides.caseIdColumn);
    const explicitCaseIdValue =
      overrides.caseId ?? (overrides.caseIdColumn ? renderCellParams.row[overrides.caseIdColumn] : undefined);
    const caseId = hasExplicitCaseId
      ? toViewerCaseId(explicitCaseIdValue)
      : getViewerCaseId(renderCellParams.row as SlidePreviewRow);

    return (
      <LinkToSlideViewerComponent
        {...overrides}
        slideId={slideId}
        formattedValue={formattedValue}
        labId={labId}
        caseId={caseId}
        caseStudyId={caseStudyId}
      />
    );
  };
};

export default renderSlideIdCell;
